import { ICoreContext } from '@msdyn365-commerce/core';
import { ColumnFilter, ColumnFiltersState, flexRender, Table } from '@tanstack/react-table';
import React, { Dispatch, SetStateAction } from 'react';
import { IOrderHistoryTableData } from '../../../actions/order-history/get-order-history.action';
import { useModal } from '../../../components/modal';
import { Pagination } from '../../../components/pagination';
import Skeleton from '../../../components/skeleton';
import {
    MULTI_VALUE_SEPARATOR,
    OPEN_STATUS,
    ORDER_HISTORY_COLUMN_ACTION,
    ORDER_HISTORY_ERROR_CODE_NO_ORDERS,
    ORDER_HISTORY_ERROR_CODE_NO_SOLD_TO,
    ORDER_HISTORY_FILTER_DELIVERY_STATUS,
    ORDER_HISTORY_FILTER_FROM_DATE,
    ORDER_HISTORY_FILTER_INVOICE_STATUS,
    ORDER_HISTORY_FILTER_LAST2YEARS,
    ORDER_HISTORY_FILTER_LAST3MONTH,
    ORDER_HISTORY_FILTER_LASTMONTH,
    ORDER_HISTORY_FILTER_TO_DATE,
    ORDER_HISTORY_SCHEDULE_LINE_CONFIRMED_DATE
} from '../../../configuration';
import { ILocalizationState } from '../../../states/localization-state';
import { dateLocaleFormatter, mapOverallDeliveryStatus, mapOverallInvoiceStatus, toLocalDate } from '../../../utilities';
import Alert from '../../../utilities/alert';
import { IOrderHistoryResources } from '../order-history.props.autogenerated';
import ExportConfigurationModal from './export-configuration-modal';
import OrderHistoryTableExpandedRow from './order-history-table-expanded-row';
import { exportToExcel } from '../../../utilities/export-to-xlsx';
import { calculatePageSizeOptions } from '../../../utilities/calculate-page-size-options';

export default function OrderHistoryTableResult({
    orderHistoryReturnCodes,
    isDataReady,
    CLASS_NAME,
    totalEntries,
    resources,
    activeButton,
    table,
    globalFilter,
    clickedFilterButton,
    localizationState,
    excelConfig,
    context,
    columnFilters,
    currentView,
    setExpanded,
    setColumnFilters,
    handleNoOpenOrdersFound,
    onConfigUpdate
}: {
    showFilter: boolean | undefined;
    orderHistoryReturnCodes: number[];
    isDataReady: boolean;
    CLASS_NAME: string;
    totalEntries: number;
    resources: IOrderHistoryResources;
    activeButton: string;
    table: Table<IOrderHistoryTableData>;
    globalFilter: string;
    clickedFilterButton: boolean;
    localizationState: ILocalizationState;
    excelConfig: string[];
    context: ICoreContext;
    columnFilters: ColumnFiltersState;
    currentView: string;
    setExpanded: Dispatch<SetStateAction<{}>>;
    setColumnFilters: Dispatch<SetStateAction<ColumnFiltersState>>;
    handleNoOpenOrdersFound: () => Promise<void>;
    onConfigUpdate: (newSettings: string[]) => void;
}) {
    const { setOpen, setClose } = useModal();

    const alertContent = orderHistoryReturnCodes?.includes(ORDER_HISTORY_ERROR_CODE_NO_SOLD_TO)
        ? { heading: resources.orderHistoryNoSoldTosMessageHeading, message: resources.orderHistoryNoSoldTosMessage }
        : orderHistoryReturnCodes?.includes(ORDER_HISTORY_ERROR_CODE_NO_ORDERS)
        ? { heading: resources.orderHistoryNoOrdersMessageHeading, message: resources.orderHistoryNoOrdersMessage }
        : null;

    const addUrlStatusParameter = (values: string[], filterId: string) => {
        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.delete(filterId);
        values.forEach(val => currentUrl.searchParams.append(filterId, val));
        window.history.pushState({}, '', currentUrl.toString());
    };

    const removeUrlParameter = (paramName: string) => {
        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.delete(paramName);
        window.history.pushState({}, '', (currentUrl as unknown) as string);
        setExpanded({});
    };

    const removeStatusFilterAndUpdate = (valueToRemove: string, filterId: string) => {
        const currentFilter = columnFilters.find(filter => filter.id === filterId);
        if (!currentFilter) {
            return;
        }

        const values = (currentFilter.value as string).split(MULTI_VALUE_SEPARATOR);
        const updatedValues = values.filter(value => value !== valueToRemove);

        //map according to filter id
        const statusKey =
            filterId === ORDER_HISTORY_FILTER_DELIVERY_STATUS
                ? mapOverallDeliveryStatus(resources).getCode(valueToRemove)
                : mapOverallInvoiceStatus(resources).getCode(valueToRemove);

        const currentUrl = new URL(window.location.href);
        const statusParams = currentUrl.searchParams.getAll(filterId);
        const filteredParams = statusParams.filter(value => value !== statusKey);

        addUrlStatusParameter(filteredParams, filterId);

        if (updatedValues.length > 0) {
            setColumnFilters([
                ...columnFilters.filter(filter => filter.id !== filterId),
                {
                    id: filterId,
                    value: updatedValues.join(MULTI_VALUE_SEPARATOR)
                }
            ]);
        } else {
            setColumnFilters(columnFilters.filter(filter => filter.id !== filterId));
        }
    };

    const getFilterLabel = (filter: string) => {
        switch (filter) {
            case ORDER_HISTORY_FILTER_LASTMONTH:
                return resources.orderHistoryFilterLastMonth;
            case ORDER_HISTORY_FILTER_LAST3MONTH:
                return resources.orderHistoryFilterLastThreeMonths;
            case ORDER_HISTORY_FILTER_LAST2YEARS:
                return resources.orderHistoryFilterLastTwoYears;
            default:
                return resources.orderHistoryFilterLastMonth;
        }
    };

    const renderNoOpenOrdersMessage = () => {
        const text = resources.orderHistoryEmptyOpenOrdersTableMessageHeading;
        const parts = text.split('{ALL_ORDERS}');

        return (
            <span>
                {parts[0]}
                <button className={`${CLASS_NAME}__inline-link-button`} onClick={handleNoOpenOrdersFound}>
                    {resources.orderHistoryEmptyOpenOrderTableUrlPlaceholder}
                </button>
                {parts[1]}
            </span>
        );
    };

    const renderExportConfigurationModal = () => {
        setOpen({
            content: (
                <ExportConfigurationModal
                    CLASS_NAME={`${CLASS_NAME}__export-configuration`}
                    table={table}
                    localizationState={localizationState}
                    resources={resources}
                    context={context}
                    excelConfig={excelConfig}
                    onClose={setClose}
                    onConfigUpdate={onConfigUpdate}
                />
            ),
            header: `${resources.orderHistoryExcelConfigurationModalHeader} - ${totalEntries} ${resources.orderHistoryExcelRowCountModalHeader}`,
            size: 'xl'
        });
    };

    return (
        <>
            {isDataReady ? (
                <div className={`${CLASS_NAME}__table-head`}>
                    {clickedFilterButton ? (
                        <div className={`${CLASS_NAME}__total-entries`}>
                            <Skeleton />
                        </div>
                    ) : (
                        <h3 className={`${CLASS_NAME}__total-entries`}>
                            {currentView === OPEN_STATUS
                                ? `${totalEntries} ${resources.orderHistoryTotalNumberOfOpenOrders}`
                                : `${totalEntries} ${resources.orderHistoryTotalNumberOfOrders} ${
                                      resources.orderHistoryPlacedLabel
                                  } ${getFilterLabel(activeButton)}`}
                        </h3>
                    )}
                    <div className={`${CLASS_NAME}__filter-section`}>
                        {columnFilters.map((filter: ColumnFilter, index: number) => (
                            <div className={`${CLASS_NAME}__active-filter`} key={index}>
                                {filter.id === ORDER_HISTORY_FILTER_DELIVERY_STATUS || filter.id === ORDER_HISTORY_FILTER_INVOICE_STATUS ? (
                                    <>
                                        <div className={`${CLASS_NAME}__active-filter-container`}>
                                            {(filter.value as string).split(MULTI_VALUE_SEPARATOR).map((value: string) => (
                                                <div className={`${CLASS_NAME}__active-filter-item`} key={value}>
                                                    {value}
                                                    <button
                                                        onClick={() => removeStatusFilterAndUpdate(value, filter.id)}
                                                        className={`${CLASS_NAME}__active-filter-item-remove-button`}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                ) : filter.id === ORDER_HISTORY_SCHEDULE_LINE_CONFIRMED_DATE ? (
                                    (() => {
                                        const [fromDate, toDate] = filter.value as string[];
                                        if (!fromDate && !toDate) {
                                            return null;
                                        }

                                        const formattedFromDate = fromDate
                                            ? dateLocaleFormatter(localizationState).formatDate(toLocalDate(fromDate))
                                            : '';
                                        const formattedToDate = toDate
                                            ? dateLocaleFormatter(localizationState).formatDate(toLocalDate(toDate))
                                            : '';

                                        let displayText = '';
                                        if (fromDate && toDate) {
                                            displayText = `${formattedFromDate} – ${formattedToDate}`;
                                        } else if (fromDate && !toDate) {
                                            displayText = `${resources.orderHistoryFilterFromLabel} ${formattedFromDate}`;
                                        } else if (!fromDate && toDate) {
                                            displayText = `${resources.orderHistoryFilterToLabel} ${formattedToDate}`;
                                        }

                                        return (
                                            <div className={`${CLASS_NAME}__active-filter`}>
                                                <div className={`${CLASS_NAME}__active-filter-container`}>
                                                    <div className={`${CLASS_NAME}__active-filter-item`}>
                                                        {displayText}
                                                        <button
                                                            onClick={() => {
                                                                const newFilters = columnFilters.find(
                                                                    (item: ColumnFilter) =>
                                                                        item.id === ORDER_HISTORY_SCHEDULE_LINE_CONFIRMED_DATE
                                                                );
                                                                if (newFilters) {
                                                                    removeUrlParameter(ORDER_HISTORY_FILTER_FROM_DATE);
                                                                    removeUrlParameter(ORDER_HISTORY_FILTER_TO_DATE);

                                                                    setColumnFilters(
                                                                        columnFilters.filter(
                                                                            filter =>
                                                                                filter.id !== ORDER_HISTORY_SCHEDULE_LINE_CONFIRMED_DATE
                                                                        )
                                                                    );
                                                                }
                                                            }}
                                                            className={`${CLASS_NAME}__active-filter-item-remove-button`}
                                                        ></button>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })()
                                ) : (
                                    <>
                                        <div className={`${CLASS_NAME}__active-filter-container`}>
                                            <div className={`${CLASS_NAME}__active-filter-item`}>
                                                {filter.value}
                                                <button
                                                    onClick={() => {
                                                        setColumnFilters(columnFilters.filter((item: ColumnFilter) => item !== filter));
                                                    }}
                                                    className={`${CLASS_NAME}__active-filter-item-remove-button`}
                                                ></button>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            ) : (
                <h3 className={`${CLASS_NAME}__total-entries`}>
                    {totalEntries} {resources.orderHistoryTotalNumberOfOrders}
                </h3>
            )}
            <div className='table-responsive'>
                <table className='table ms-order-history__table'>
                    <thead>
                        {clickedFilterButton ? (
                            <tr>
                                {table.getVisibleFlatColumns().map(col => (
                                    <th key={col.id}>
                                        <Skeleton />
                                    </th>
                                ))}
                            </tr>
                        ) : (
                            table.getHeaderGroups().map(headerGroup => (
                                <tr key={headerGroup.id}>
                                    {headerGroup.headers.map(header => {
                                        const sortValue =
                                            { asc: 'msc-table-sorting--asc', desc: 'msc-table-sorting--desc' }[
                                                header.column.getIsSorted() as string
                                            ] ?? null;
                                        return (
                                            <th key={header.id} colSpan={header.colSpan}>
                                                {header.isPlaceholder ? null : (
                                                    <div
                                                        className={
                                                            header.column.getCanSort()
                                                                ? `msc-table-sorting ${sortValue ?? 'msc-table-sorting--default'} `
                                                                : ''
                                                        }
                                                        onClick={header.column.getToggleSortingHandler()}
                                                    >
                                                        {flexRender(header.column.columnDef.header, header.getContext())}
                                                    </div>
                                                )}
                                            </th>
                                        );
                                    })}
                                </tr>
                            ))
                        )}
                    </thead>
                    <tbody key={table.getState().pagination.pageIndex} className='fade-slide-right'>
                        {alertContent ? (
                            <td colSpan={8} className='no-padding'>
                                <Alert heading={resources.orderHistoryNoOrdersMessageHeading} />
                            </td>
                        ) : table.getRowModel().rows.length === 0 ? (
                            <td colSpan={8} className='no-padding'>
                                <div className='alert alert-info' role='alert'>
                                    {currentView === OPEN_STATUS
                                        ? renderNoOpenOrdersMessage()
                                        : resources.orderHistoryEmptyOrderHistoryTableMessageHeading}
                                </div>
                            </td>
                        ) : (
                            table.getRowModel().rows.map((row, index) => {
                                const isEven = index % 2 === 1;
                                const rowClass = `parent ${isEven ? 'even-parent' : 'odd-parent'}`;
                                return (
                                    <React.Fragment key={row.id}>
                                        <tr
                                            className={rowClass}
                                            onClick={() => {
                                                setExpanded((old: { [x: string]: any }) => ({
                                                    ...old,
                                                    [row.id]: !old[row.id]
                                                }));
                                            }}
                                        >
                                            {row.getVisibleCells().map(cell => {
                                                const highlightClass =
                                                    cell.column.id === ORDER_HISTORY_COLUMN_ACTION &&
                                                    row.original.highlightOrderLines &&
                                                    globalFilter !== ''
                                                        ? `ms-order-history__highlight-search-term`
                                                        : '';
                                                if (cell.column.id === ORDER_HISTORY_COLUMN_ACTION) {
                                                    return (
                                                        <>
                                                            {!clickedFilterButton ? (
                                                                <td key={cell.id}>
                                                                    <span className={`${highlightClass} ms-order-history__table-action`}>
                                                                        <a
                                                                            className={`${CLASS_NAME}__table-collapse`}
                                                                            data-open={row.getIsExpanded()}
                                                                            onClick={e => {
                                                                                e.preventDefault();
                                                                                e.stopPropagation();
                                                                                setExpanded((old: { [x: string]: any }) => ({
                                                                                    ...old,
                                                                                    [row.id]: !old[row.id]
                                                                                }));
                                                                            }}
                                                                        ></a>
                                                                    </span>
                                                                </td>
                                                            ) : (
                                                                <td key={cell.id}>
                                                                    <Skeleton />
                                                                </td>
                                                            )}
                                                        </>
                                                    );
                                                } else {
                                                    return (
                                                        <>
                                                            {clickedFilterButton ? (
                                                                <td key={cell.id}>
                                                                    <Skeleton />
                                                                </td>
                                                            ) : (
                                                                <td key={cell.id}>
                                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                                </td>
                                                            )}
                                                        </>
                                                    );
                                                }
                                            })}
                                        </tr>
                                        {row.getIsExpanded() && (
                                            <OrderHistoryTableExpandedRow
                                                resources={resources}
                                                isEven={isEven}
                                                row={row}
                                                globalFilter={globalFilter}
                                                context={context}
                                                localizationState={localizationState}
                                                CLASS_NAME={CLASS_NAME}
                                            />
                                        )}
                                    </React.Fragment>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
            <div className={`${CLASS_NAME}__table-bottom`}>
                <div className={`${CLASS_NAME}__export-split-wrapper`}>
                    <button
                        className={`${CLASS_NAME}__export-button`}
                        aria-label={resources.orderHistoryExportAsExcelBtnLabel}
                        title={resources.orderHistoryExportAsExcelBtnLabel}
                        onClick={() => {
                            void exportToExcel(table.getSortedRowModel().rows, localizationState, resources, excelConfig);
                        }}
                    >
                        <span className={`${CLASS_NAME}__export-button-icon`} />
                        {resources.orderHistoryExportAsExcelBtnTitle}
                    </button>

                    <button
                        className={`${CLASS_NAME}__export-config-button`}
                        aria-label={resources.orderHistoryExportAsExcelConfigBtnLabel}
                        title={resources.orderHistoryExportAsExcelConfigBtnLabel}
                        onClick={() => {
                            renderExportConfigurationModal();
                        }}
                    >
                        <span className={`${CLASS_NAME}__export-config-button-icon`} />
                    </button>
                </div>
                <Pagination
                    pageIndex={table.getState().pagination.pageIndex}
                    resources={resources}
                    table={table}
                    pageSizeOptions={calculatePageSizeOptions(table.getFilteredRowModel().rows.length)}
                    componentName={OrderHistoryTableResult.name}
                />
            </div>
        </>
    );
}
